/**
 * Scenarios for the "Classifying Together" sorting activity.
 *
 * Each scenario needs:
 *   id          unique, URL-safe name
 *   text        the scenario students read
 *   answer      'rule-based' or 'data-driven'
 *   explanation one sentence shown after the student answers
 *   whyNot      why the other kind of system would be a worse fit, naming
 *               a risk and who it affects
 *   emoji       shown on the placeholder image
 *   imageAlt    describes the image for screen readers
 *   image       optional path to a real image; replaces the placeholder
 */
export default [
	{
		id: 'school-bell',
		text: 'The school bell rings at 8:00, 8:45, 9:30, and 10:15 every day.',
		answer: 'rule-based',
		explanation: 'The bell follows a schedule a person set up. It rings at the same times every day, no matter what.',
		whyNot: 'There’s nothing to learn here because the times are already decided, and a system that guessed could ring at the wrong time and leave students and teachers confused about where to be.',
		emoji: '🔔',
		imageAlt: 'A school bell mounted on a brick wall.',
	},
	{
		id: 'password-checker',
		text: 'When you make an account for a game, the site keeps saying “Password too weak” until you add a number.',
		answer: 'rule-based',
		explanation: 'Someone wrote a rule that every password needs a number. The site checks every password the same way.',
		whyNot: 'If a system guessed which passwords were safe instead of using a clear rule, people making accounts wouldn’t know what to fix, and weak passwords could slip through and put their accounts at risk.',
		emoji: '🔒',
		imageAlt: 'A padlock next to a password box on a sign-up form.',
	},
	{
		id: 'thermostat',
		text: 'A smart thermostat turns the heat on when the classroom drops below 20°C and turns it off when it reaches 22°C.',
		answer: 'rule-based',
		explanation: 'Even though it’s called “smart,” it follows an “if this, then that” rule. It never changes on its own.',
		whyNot: 'One clear temperature rule already works, and a system that learned on its own could guess wrong and leave students too cold or too hot to focus.',
		emoji: '🌡️',
		imageAlt: 'A thermostat on a classroom wall.',
	},
	{
		id: 'vending-machine',
		text: 'You enter B4 into a vending machine, and it drops a bag of chips.',
		answer: 'rule-based',
		explanation: 'Someone programmed the machine so that B4 always means chips. It follows the same steps every time.',
		whyNot: 'Customers expect B4 to mean chips every time, and a system that guessed what they wanted could give them the wrong snack after they’ve paid.',
		emoji: '🥤',
		imageAlt: 'A vending machine filled with drinks and snacks.',
	},
	{
		id: 'crosswalk',
		text: 'When you press the crosswalk button, the light changes to “Walk” and gives you 30 seconds to cross.',
		answer: 'rule-based',
		explanation: 'Pressing the button always leads to the same result. The timing was set by a person.',
		whyNot: 'People crossing need the light to be predictable, and a system that changed the timing on its own could leave someone who walks slowly, like an older adult or a wheelchair user, without enough time.',
		emoji: '🚦',
		imageAlt: 'A crosswalk signal at a street corner.',
	},
	{
		id: 'late-fee',
		text: 'Your library app charges you 25 cents for every day a book is late.',
		answer: 'rule-based',
		explanation: 'The app uses a simple formula a person wrote: days late × 25 cents.',
		whyNot: 'Fees need to be the same for everyone, and a system that learned from data could end up charging some library users more than others, which wouldn’t be fair.',
		emoji: '📚',
		imageAlt: 'A stack of library books with a due-date slip.',
	},
	{
		id: 'music-recs',
		text: 'A music app makes you a playlist of songs you’ve never heard before, and you end up liking most of them.',
		answer: 'data-driven',
		explanation: 'No one wrote a rule for your exact taste. The app compares what you play with what lots of other listeners play and finds patterns.',
		whyNot: 'No one could write rules for every listener’s taste, so a rule-based app would give everyone the same few suggestions and listeners with less common taste would rarely find new songs they like.',
		emoji: '🎧',
		imageAlt: 'Headphones next to a phone showing a playlist.',
	},
	{
		id: 'plant-identifier',
		text: 'On a hike, you take a photo of a plant, and an app tells you what kind of plant it is.',
		answer: 'data-driven',
		explanation: 'Plants come in too many shapes and colors to write rules for. The app learned from thousands of plant photos.',
		whyNot: 'Plants look different depending on the light, angle, and season, so written rules would miss many of them and could give hikers a wrong answer about a plant that might be harmful.',
		emoji: '🌿',
		imageAlt: 'A phone camera pointed at a leafy plant.',
	},
	{
		id: 'next-word',
		text: 'As you text, your keyboard suggests the next word, and over time the suggestions start to sound more like you.',
		answer: 'data-driven',
		explanation: 'The keyboard learns from what you and many other people type, so its suggestions change over time.',
		whyNot: 'Rules can’t keep up with how each person writes, including slang and new words, so the suggestions would feel the same for everyone and be least helpful for people whose writing doesn’t match the rules.',
		emoji: '⌨️',
		imageAlt: 'A phone keyboard showing suggested next words.',
	},
]
